"use client";

import { sendEmail } from "@/actions/sendEmail";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useTransition, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { createContactFormSchema } from "@/lib/schema";
import { Spinner } from "@/components/icons/icons";
import { Turnstile } from "@marsidev/react-turnstile";
import { useTranslations } from "next-intl";

// type FormInputs = z.infer<typeof ContactFormSchema>;

export default function ContactForm() {
  const t = useTranslations("ContactForm");
  const [isPending, startTransition] = useTransition();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const ContactFormSchema = createContactFormSchema(t);
    type FormInputs = z.infer<typeof ContactFormSchema>;

  const form = useForm<FormInputs>({
    resolver: zodResolver(ContactFormSchema),
    defaultValues: {
      name: "",
      namaperusahaan: "",
      jabatan: "",
      email: "",
      message: "",
    },
  });

  async function onSubmit(data: FormInputs) {
    if (!turnstileToken) {
      toast.error(t("error.errorCaptcha"));
      return;
    }

    startTransition(async () => {
      // Verifikasi Turnstile token sebelum mengirim email
      const verifyResponse = await fetch("/api/verify-turnstile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ turnstileToken }),
      });

      const verifyData = await verifyResponse.json();

      if (!verifyResponse.ok) {
        toast.error(verifyData.error || t("error.errorCaptchaVerification"));
        return;
      }

      // Jika CAPTCHA berhasil diverifikasi, kirim email
      const response = await sendEmail(data);

      if (response.success) {
        toast.success(t("success.messageSent"));
        form.reset();
        setTurnstileToken(null); // Reset token setelah pengiriman
        return;
      }

      if (response.error) {
        toast.error(response.error);
      }
    });
  }

  return (
    <div className="relative z-10 mx-auto -mt-20 max-w-4xl rounded-md bg-white px-[7%] py-10 shadow-lg dark:bg-slate-900 dark:shadow-slate-950/40 md:py-[5%]">
      <div className="container">
        <ul className="mb-6 space-y-1 text-center text-sm">
          <li>
            <address className="-mt-px leading-6">
              {t("address")}
            </address>
          </li>
          <li>
            <span>WhatsApp: </span>
            <a
              href="https://wa.me/62811143352?text=Hello!%20I%20am%20interested%20in%20the%20services%20offered%20by%20*Mubarokah*."
              className="hover:text-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              +62 811-1433-52
            </a>
          </li>
          <li>
            <span>Email: </span>
            <a href="mailto:hey@mubarokah.com" className="hover:text-primary">
              hey@mubarokah.com
            </a>
          </li>
        </ul>
        <Form {...form}>
          <form
            className="flex flex-col dark:text-black"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="mb-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-white">{t("name")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-wrap lg:flex-nowrap lg:space-x-8">
              <div className="mb-6 w-full lg:w-1/2">
                <FormField
                  control={form.control}
                  name="namaperusahaan"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="dark:text-white">
                        {t("companyName")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mb-6 w-full lg:w-1/2">
                <FormField
                  control={form.control}
                  name="jabatan"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="dark:text-white">
                        {t("jobTitle")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <div className="mb-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-white">{t("email")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mb-6">
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-white">{t("message")}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("messagePlaceholder")}
                        rows={6}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Turnstile CAPTCHA */}
            <div className="mb-6">
              <Turnstile
                siteKey="0x4AAAAAAA29-0PYk7u7JLd5"
                onSuccess={(token) => setTurnstileToken(token)}
              />
            </div>

            <Button type="submit" size="lg" disabled={isPending}>
              {isPending ? (
                <>
                  <Spinner className="mr-2 h-5 w-5 animate-spin" />
                  <span>{t("sendMessage")}</span>
                </>
              ) : (
                <span>{t("sendMessage")}</span>
              )}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
